import { Permission } from './models/Permissions';

export interface PagingRequest {
  limit: number;
  cursor: string | null;
}

export enum CloudProvider {
  AZURE = 'Azure',
  AWS = 'AWS',
  AWS_OCI = 'AWS_OCI',
  Gov_AWS = 'Gov_AWS',
  Gov_Azure = 'Gov_Azure',
}

/** Aligns with AMP service model.InstanceStatus JSON values. */
export enum InstanceStatus {
  Creating = 'Creating',
  Active = 'Active',
  /** An on-prem deployment whose license was downloaded but which AMB has not onboarded. */
  Licensed = 'Licensed',
  Deactivating = 'Deactivating',
  Deactivated = 'Deactivated',
  Reactivating = 'Reactivating',
  Deleting = 'Deleting',
  Deleted = 'Deleted',
  Unknown = 'Unknown',
}

export type TaniumInstance = {
  id: string;
  licenseId: string;
  createdAt: string;
  friendlyName?: string;
  url: string;
  instanceName: string;
  region: string;
  cloudProvider?: CloudProvider;
  isCloudInstance: boolean;
  status?: InstanceStatus;
  deletedAt?: string;
  isUp?: boolean;
  isInMaintenanceWindow?: boolean;
  endpointCount?: number;
  licenseExpirationDate?: string;
  downloadable?: boolean;
  // The fields below are populated only for "available" (pending) instances — a license offered to this
  // tenant by verified-email match but not yet downloaded/linked. Downloading promotes it to a linked instance.
  isAvailable?: boolean;
  notifiedAt?: string;
  customerName?: string;
  sdrId?: string;
  // The deployment's hostname(s) from the offered license
  hostnames?: string;
};

/** An on-prem license the tenant can download, from AMP service downloadableLicenseResponse. */
export type DownloadableLicense = {
  licenseId: number;
  /** Empty for an available (pending) license, which has no linked instance yet. */
  instanceId?: string;
  customerName?: string;
  url?: string;
  friendlyName?: string;
  /** Date-only string, e.g. "2027-01-15". */
  expiresAt?: string;
  notifiedAt?: string;
  /** Absent until the license has been downloaded at least once. */
  downloadedAt?: string;
  expired?: boolean;
  /** A pending offer; downloading it links the instance to this tenant. */
  isAvailable?: boolean;
  sdrId?: string;
  /** The deployment's hostname(s), standing in for `url` on a pending offer. */
  hostnames?: string;
};

export type CreateMspInstanceRequest = {
  customerName: string;
  domain: string;
  cloudRegion: string;
};

export type CreateMspInstanceResponse = {
  licenseId: number;
};

export type CEMutationResponse = {
  ceId: number;
  status: InstanceStatus;
  deactivatedAt?: string;
  reactivatedAt?: string;
};

export interface OidcMetadataRequest {
  email?: string;
  username?: string;
  workspace?: string;
}
export interface OidcMetadataResponse {
  oidcMetadataUrl: string;
  tokenEndpoint: string;
  authEndpoint: string;
  clientId: string;
  isMultiTenantDomain?: boolean;
}

export enum SessionKeys {
  AmpToken = 'amp_token',
  AmpIdToken = 'amp_id_token',
  AmpRefreshToken = 'amp_refresh_token',
  AmpTokenExpiration = 'amp_token_expiration',
  Verifier = 'verifier',
  Auth = 'auth',
  Email = 'email',
  OidcMeta = 'oidc_meta',
}

export interface PermissionsResponse {
  permissions: Permission[];
}

export interface EndpointCountResponse {
  endpointCount: number;
}

/** Whether anyone can sign in to the console of a cloud instance. Aligns with the AMP service JSON values. */
export enum ConsoleAccessStatus {
  Configured = 'configured',
  NotConfigured = 'notConfigured',
  Unknown = 'unknown',
}

export type ConsoleAccessStatusEntry = { instanceId: string; consoleAccess: ConsoleAccessStatus };

export enum ComplianceFramework {
  None = 'None',
  USGov = 'USGov',
  ProtectedB = 'ProtectedB',
}

export interface MetricDataPoint {
  date: string; // YYYY-MM-DD
  value: number;
}

export interface InstanceMetricHistory {
  instanceId: string;
  instanceName: string;
  friendlyName: string;
  data: MetricDataPoint[];
}

export interface AggregatedMetricDataPoint {
  date: string;
  totalValue: number;
}

export interface MetricHistory {
  metricType: string;
  instances: InstanceMetricHistory[];
  aggregated: AggregatedMetricDataPoint[];
}
